import { call } from '@advanza/api'
import { Col, Row } from '@advanza/grid'
import { SelectSimple } from '@advanza/input'
import style from '@advanza/input/inputField.module.css'
import { Button, Divider } from '@advanza/ui'
import LoadingDots from '@trustoo/components/ui/LoadingDots'
import { useChangeEntity } from 'hooks/entityHooks'
import { useOptions } from 'hooks/optionHooks'
import ExpectationSetting from 'interfaces/ExpectationSetting'
import Option from 'interfaces/Options'
import { Fragment } from 'react'
import { AppDispatch, RootState } from 'types/RootStateType'

export const EXP_SET_BOUND = 10000

const COVERAGE_LEVEL_CORE = 'core'
const SEQUENCE_COVERAGE_LEVELS = ['low', 'medium', 'high'] as const
const CAMPAIGN_NAME_COVERAGE_LEVELS = [...SEQUENCE_COVERAGE_LEVELS, COVERAGE_LEVEL_CORE] as const

const EXP_SET_FIELDS = {
    ...Object.fromEntries(
        CAMPAIGN_NAME_COVERAGE_LEVELS.map((coverageLevel, i) => [
            'threshold_' + coverageLevel,
            {
                type: 'float',
                placeholder: 'Threshold ' + coverageLevel,
                min: 0,
                fallback: null,
                errorMsg: 'Check that the thresholds are in ascending order (and >= 0)',
                validator: (threshold: number | null, expSet: ExpectationSetting) => {
                    if (threshold === null) {
                        return true
                    }
                    if (threshold < 0) {
                        return false
                    }
                    if (coverageLevel === COVERAGE_LEVEL_CORE) {
                        return true
                    }
                    const lowerThresholdsOk = SEQUENCE_COVERAGE_LEVELS.slice(0, i)
                        .map((levelLower) => expSet[`threshold_${levelLower}`])
                        .every(
                            (thresholdLower) =>
                                thresholdLower === null || thresholdLower <= threshold,
                        )
                    const higherThresholdsOk = SEQUENCE_COVERAGE_LEVELS.slice(i + 1)
                        .map((levelHigher) => expSet[`threshold_${levelHigher}`])
                        .every(
                            (thresholdHigher) =>
                                thresholdHigher === null || thresholdHigher >= threshold,
                        )
                    return lowerThresholdsOk && higherThresholdsOk
                },
            },
        ]),
    ),
    region_optimizer_use_avg_coverage: {
        type: 'boolean',
        msg: 'Region optimizer use avg coverage',
    },
    changeNote: {
        type: 'text',
        textArea: true,
        placeholder: 'Add a change note (optional)',
        legend: true,
        maxLength: 255,
    },
}

const saveExpectationSetting =
    (expectationSettingId: number) => (_dispatch: AppDispatch, getState: () => RootState) => {
        const expectationSetting =
            getState().expectationSettings.entities.expectationSettings[expectationSettingId]
        return call('office/expectation-settings/save', { json: [expectationSetting] })
    }

interface Props {
    expectationSettingId: number
}

const ExpectationSettingEntity = ({ expectationSettingId }: Props) => {
    const {
        entity: expectationSetting,
        onSaveEntity,
        onChangeEntity,
        renderInput,
    } = useChangeEntity(
        {
            store: 'expectationSettings',
            name: 'expectationSettings',
            entityId: expectationSettingId,
            saveFunc: saveExpectationSetting,
        },
        EXP_SET_FIELDS,
    )
    const isMainExpSet = expectationSettingId < EXP_SET_BOUND
    const options = (useOptions(expectationSetting.site_id, true).options || {}) as {
        [optionId: number]: Option
    }
    const selectOptions = Object.values(options).map((option) => ({
        value: option.option_id,
        name: option.value,
    }))

    const renderSelect = (field: string, placeholder: string) => (
        <SelectSimple
            legend
            placeholder={placeholder}
            alwaysControlValue
            emptyOption="(None)"
            options={selectOptions}
            value={expectationSetting[field] || '-'}
            onChange={(e: any) => {
                const optionId = e.target.value === null ? 0 : parseInt(e.target.value, 10)
                onChangeEntity({
                    [field]: optionId,
                    [field.replace('filter', '').replace('_id', 'Name')]: optionId
                        ? options[optionId].value
                        : null,
                })
            }}
        />
    )

    return (
        <div className="p-4">
            {isMainExpSet ? (
                renderSelect('filteroption_id', 'Filteroption 1')
            ) : (
                <>
                    <div className={style.legend}>Filteroption 1</div>
                    {expectationSetting.optionName}
                </>
            )}
            <Divider m />
            {renderSelect('filteroption2_id', 'Filteroption 2')}
            <Divider m />
            {renderSelect('filteroption3_id', 'Filteroption 3')}
            <Divider m />
            {CAMPAIGN_NAME_COVERAGE_LEVELS.map((coverageLevel) => (
                <Fragment key={coverageLevel}>
                    {renderInput('threshold_' + coverageLevel)}
                    <Divider m />
                </Fragment>
            ))}
            {isMainExpSet && (
                <>
                    {renderInput('region_optimizer_use_avg_coverage')}
                    <Divider m />
                </>
            )}
            {renderInput('changeNote')}
            <Divider m />
            <Row end="xs">
                <Col x>
                    <Button
                        disabled={expectationSetting._saving}
                        onClick={() =>
                            onSaveEntity!().then(
                                () => onChangeEntity({ _errors: {} }), // TODO: _validate() should clear _errors on success
                                () => {},
                            )
                        }>
                        {expectationSetting._saving ? <LoadingDots color="#fff" /> : 'Save'}
                    </Button>
                </Col>
            </Row>
        </div>
    )
}

export default ExpectationSettingEntity
